import React, { useState, useEffect } from "react";
import { Download } from "lucide-react";
import { Modal } from "../common/ui/modal";
import Button from "../common/ui/button/Button";
import { ImagePreview } from "./ImagePreview";
import { PdfPreview } from "./PdfPreview";
import { TextPreview } from "./TextPreview";
import { TooLargePreview } from "./TooLargePreview";
import { isPreviewable, PreviewType } from "../../utils/filePreview";
import { FILE_PREVIEW_LIMITS } from "../../constants/filePreview";
import { apiClient } from "../../utils/apiClient";

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: string | null;
  fileName: string | null;
  fileSize: number | null;
  mimeType: string | null;
  onDownload: () => void;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileId,
  fileName,
  fileSize,
  mimeType,
  onDownload,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<PreviewType>(null);
  const [canPreview, setCanPreview] = useState(false);
  const [tooLarge, setTooLarge] = useState(false);

  useEffect(() => {
    if (!isOpen || !fileId || !fileName || fileSize === null || !mimeType) {
      return;
    }

    // Check if file is previewable
    const eligibility = isPreviewable(mimeType, fileSize);
    setPreviewType(eligibility.previewType);
    setCanPreview(eligibility.canPreview);

    // If file type is supported but too large, show the too large preview
    if (!eligibility.canPreview && eligibility.previewType !== null) {
      setTooLarge(true);
      return;
    }

    // If not previewable at all
    if (!eligibility.canPreview) {
      setError(eligibility.reason || "File cannot be previewed");
      return;
    }

    // Fetch the file for preview
    const fetchFile = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.downloadFileResponse(fileId, true);

        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }

        // For images and PDFs, create a blob URL
        if (
          eligibility.previewType === "image" ||
          eligibility.previewType === "pdf"
        ) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setFileContent(url);
        } else if (eligibility.previewType === "text") {
          // For text files, read as text
          const text = await response.text();
          setFileContent(text);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load file preview"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFile();

    // Cleanup blob URLs when modal closes
    return () => {
      if (fileContent && (previewType === "image" || previewType === "pdf")) {
        URL.revokeObjectURL(fileContent);
      }
    };
  }, [isOpen, fileId, fileName, fileSize, mimeType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFileContent(null);
      setError(null);
      setLoading(false);
      setTooLarge(false);
      setCanPreview(false);
      setPreviewType(null);
    }
  }, [isOpen]);

  const getSizeLimit = () => {
    if (previewType === "image") return FILE_PREVIEW_LIMITS.image;
    if (previewType === "pdf") return FILE_PREVIEW_LIMITS.pdf;
    if (previewType === "text") return FILE_PREVIEW_LIMITS.text;
    return 0;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isFullscreen={true}
      title={fileName || "File Preview"}
      headerActions={
        <Button
          variant="outline"
          size="md"
          onClick={onDownload}
          startIcon={<Download className="h-4 w-4" />}
        >
          Download
        </Button>
      }
    >
      <div className="flex-1 overflow-hidden">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-600 dark:text-gray-400">
              Loading preview...
            </div>
          </div>
        )}

        {error && (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <Button
                variant="primary"
                onClick={onDownload}
                startIcon={<Download className="h-4 w-4" />}
              >
                Download File Instead
              </Button>
            </div>
          </div>
        )}

        {tooLarge && fileName && fileSize !== null && (
          <TooLargePreview
            fileName={fileName}
            fileSize={fileSize}
            sizeLimit={getSizeLimit()}
            onDownload={onDownload}
          />
        )}

        {!loading &&
          !error &&
          !tooLarge &&
          canPreview &&
          fileContent &&
          fileName && (
            <>
              {previewType === "image" && (
                <ImagePreview fileUrl={fileContent} fileName={fileName} />
              )}
              {previewType === "pdf" && (
                <PdfPreview fileUrl={fileContent} fileName={fileName} />
              )}
              {previewType === "text" && (
                <TextPreview content={fileContent} fileName={fileName} />
              )}
            </>
          )}
      </div>
    </Modal>
  );
};
